import { ResumeTemplate } from '../lib/resumeTemplateService';

interface ResumeData {
  personal?: {
    fullName: string;
    email: string;
    phone: string;
    location: string;
  };
  summary?: string;
  experience?: Array<{
    company: string;
    title: string;
    startDate: string;
    endDate?: string;
    bullets?: string[];
  }>;
  education?: Array<{
    school: string;
    degree: string;
    field: string;
    graduationDate: string;
  }>;
  skills?: {
    technical?: string[];
    soft?: string[];
    other?: string[];
  } | string[];
  certifications?: string[];
}

interface ResumeTemplateRendererProps {
  template: ResumeTemplate;
  resumeData: ResumeData;
  scale?: number;
}

export function ResumeTemplateRenderer({ template, resumeData, scale = 1 }: ResumeTemplateRendererProps) {
  // Defensive null checks with fallback values
  if (!template) {
    return <div style={{ padding: '20px', color: 'red' }}>Error: Template not found</div>;
  }

  const colorScheme = template.color_schemes?.[0] || {
    background: '#ffffff',
    text: '#000000',
    primary: '#1e40af',
    secondary: '#64748b',
  };

  const fontSettings = template.font_settings || {
    heading: 'Arial, sans-serif',
    body: 'Arial, sans-serif',
    size: { heading: 24, subheading: 14, body: 11 },
    lineHeight: { heading: 1.2, subheading: 1.3, body: 1.6 },
  };

  const templateData = template.template_data || {
    headerStyle: 'left',
    spacing: { section: 18, item: 12 },
  };

  const spacing = {
    section: templateData.spacing?.section ?? 18,
    item: templateData.spacing?.item ?? 12,
  };

  const containerStyle: React.CSSProperties = {
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    width: '8.5in',
    minHeight: '11in',
    backgroundColor: colorScheme.background,
    color: colorScheme.text,
    fontFamily: fontSettings.body,
    fontSize: `${fontSettings.size.body}pt`,
    lineHeight: fontSettings.lineHeight?.body || 1.6,
    padding: '0.75in',
    boxSizing: 'border-box',
    position: 'relative',
  };

  const renderHeader = () => {
    if (!resumeData.personal) return null;

    const headerStyle: React.CSSProperties = {
      textAlign: templateData.headerStyle === 'centered' ? 'center' : 'left',
      marginBottom: `${spacing.section}pt`,
      paddingBottom: templateData.headerStyle === 'accent-bar' ? '10pt' : '0',
      borderBottom: templateData.headerStyle === 'accent-bar' ? `3pt solid ${colorScheme.primary}` : 'none',
    };

    const nameStyle: React.CSSProperties = {
      fontFamily: fontSettings.heading,
      fontSize: `${fontSettings.size.heading}pt`,
      lineHeight: fontSettings.lineHeight?.heading || 1.2,
      color: colorScheme.primary,
      fontWeight: 'bold',
      marginBottom: '6pt',
    };

    const contactStyle: React.CSSProperties = {
      fontSize: `${fontSettings.size.body}pt`,
      color: colorScheme.secondary,
      display: 'flex',
      justifyContent: templateData.headerStyle === 'centered' ? 'center' : 'flex-start',
      gap: '15pt',
      flexWrap: 'wrap',
    };

    return (
      <div style={headerStyle}>
        <div style={nameStyle}>{resumeData.personal.fullName}</div>
        <div style={contactStyle}>
          <span>{resumeData.personal.email}</span>
          <span>•</span>
          <span>{resumeData.personal.phone}</span>
          <span>•</span>
          <span>{resumeData.personal.location}</span>
        </div>
      </div>
    );
  };

  const renderSectionTitle = (title: string) => {
    const titleStyle: React.CSSProperties = {
      fontFamily: fontSettings.heading,
      fontSize: `${fontSettings.size.subheading}pt`,
      color: colorScheme.primary,
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.5pt',
      marginBottom: `${spacing.item}pt`,
      paddingBottom: '4pt',
      borderBottom: `1pt solid ${colorScheme.secondary}`,
    };

    return <div style={titleStyle}>{title}</div>;
  };

  const renderSummary = () => {
    if (!resumeData.summary) return null;

    const sectionStyle: React.CSSProperties = {
      marginBottom: `${spacing.section}pt`,
    };

    return (
      <div style={sectionStyle}>
        {renderSectionTitle('Professional Summary')}
        <div style={{ textAlign: 'justify' }}>{resumeData.summary}</div>
      </div>
    );
  };

  const renderExperience = () => {
    if (!resumeData.experience || resumeData.experience.length === 0) return null;

    const sectionStyle: React.CSSProperties = {
      marginBottom: `${spacing.section}pt`,
    };

    const entryStyle: React.CSSProperties = {
      marginBottom: `${spacing.item}pt`,
    };

    const titleLineStyle: React.CSSProperties = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      marginBottom: '4pt',
    };

    const jobTitleStyle: React.CSSProperties = {
      fontWeight: 'bold',
      fontSize: `${fontSettings.size.body + 0.5}pt`,
      color: colorScheme.text,
    };

    const companyStyle: React.CSSProperties = {
      fontWeight: 'bold',
      color: colorScheme.secondary,
    };

    const dateStyle: React.CSSProperties = {
      fontSize: `${fontSettings.size.body - 0.5}pt`,
      color: colorScheme.secondary,
      fontStyle: 'italic',
    };

    return (
      <div style={sectionStyle}>
        {renderSectionTitle('Professional Experience')}
        {resumeData.experience.map((exp, index) => (
          <div key={index} style={entryStyle}>
            <div style={titleLineStyle}>
              <div>
                <div style={jobTitleStyle}>{exp.title}</div>
                <div style={companyStyle}>{exp.company}</div>
              </div>
              <div style={dateStyle}>
                {exp.startDate} - {exp.endDate || 'Present'}
              </div>
            </div>
            {exp.bullets && exp.bullets.length > 0 && (
              <ul style={{ margin: '6pt 0 0 15pt', paddingLeft: '10pt' }}>
                {exp.bullets.map((bullet, i) => (
                  <li key={i} style={{ marginBottom: '3pt' }}>
                    {bullet}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderEducation = () => {
    if (!resumeData.education || resumeData.education.length === 0) return null;

    const sectionStyle: React.CSSProperties = {
      marginBottom: `${spacing.section}pt`,
    };

    const entryStyle: React.CSSProperties = {
      marginBottom: `${spacing.item}pt`,
    };

    const degreeStyle: React.CSSProperties = {
      fontWeight: 'bold',
      fontSize: `${fontSettings.size.body + 0.5}pt`,
    };

    const schoolStyle: React.CSSProperties = {
      color: colorScheme.secondary,
    };

    return (
      <div style={sectionStyle}>
        {renderSectionTitle('Education')}
        {resumeData.education.map((edu, index) => (
          <div key={index} style={entryStyle}>
            <div style={degreeStyle}>
              {edu.degree} in {edu.field}
            </div>
            <div style={schoolStyle}>
              {edu.school} • {edu.graduationDate}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSkills = () => {
    const skills = resumeData.skills;
    if (!skills) return null;

    let allSkills: string[] = [];
    if (Array.isArray(skills)) {
      allSkills = skills;
    } else {
      allSkills = [
        ...(skills.technical || []),
        ...(skills.soft || []),
        ...(skills.other || []),
      ];
    }

    if (allSkills.length === 0) return null;

    const sectionStyle: React.CSSProperties = {
      marginBottom: `${spacing.section}pt`,
    };

    const skillsGridStyle: React.CSSProperties = {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8pt',
    };

    const skillBadgeStyle: React.CSSProperties = {
      backgroundColor: `${colorScheme.primary}15`,
      color: colorScheme.primary,
      padding: '4pt 10pt',
      borderRadius: '3pt',
      fontSize: `${fontSettings.size.body - 0.5}pt`,
      fontWeight: '500',
    };

    return (
      <div style={sectionStyle}>
        {renderSectionTitle('Skills')}
        <div style={skillsGridStyle}>
          {allSkills.filter(s => s.trim()).map((skill, index) => (
            <span key={index} style={skillBadgeStyle}>
              {skill}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderCertifications = () => {
    if (!resumeData.certifications || resumeData.certifications.length === 0) return null;

    const sectionStyle: React.CSSProperties = {
      marginBottom: `${spacing.section}pt`,
    };

    return (
      <div style={sectionStyle}>
        {renderSectionTitle('Certifications')}
        <ul style={{ margin: '6pt 0 0 15pt', paddingLeft: '10pt' }}>
          {resumeData.certifications.filter(c => c.trim()).map((cert, index) => (
            <li key={index} style={{ marginBottom: '3pt' }}>
              {cert}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderContent = () => {
    if (template.layout_type === 'two_column' || template.layout_type === 'sidebar') {
      return (
        <div style={{ display: 'flex', gap: '30pt' }}>
          <div style={{ flex: '0 0 250pt' }}>
            {renderSkills()}
            {renderEducation()}
            {renderCertifications()}
          </div>
          <div style={{ flex: 1 }}>
            {renderSummary()}
            {renderExperience()}
          </div>
        </div>
      );
    }

    return (
      <>
        {renderSummary()}
        {renderExperience()}
        {renderEducation()}
        {renderSkills()}
        {renderCertifications()}
      </>
    );
  };

  return (
    <div style={containerStyle} className="resume-template-render">
      {renderHeader()}
      {renderContent()}
    </div>
  );
}
